import {
    Alert,
    Button,
    Card,
    Checkbox,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Snackbar,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TablePagination,
    TableRow,
    TextField,
    Typography,
    Chip,
    IconButton,
    CircularProgress,
    Box
} from '@mui/material';
import { useEffect, useState } from 'react';
import Iconify from '../components/Iconify';
import Page from '../components/Page';
import { UserListHead, UserListToolbar } from '../sections/@dashboard/user';

// --------------------------------------------------------------------------
// 1. API HOOK (Logic for GET, POST, PUT, DELETE)
// --------------------------------------------------------------------------

const apiFetch = async (url, options = {}) => {
    const response = await fetch(url, {
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        ...options,
        body: options.body ? JSON.stringify(options.body) : undefined,
    });

    if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `API failed with status ${response.status}`;
        try {
            const errorData = JSON.parse(errorText);
            errorMessage = errorData.message || errorData.error || errorMessage;
        } catch (e) { /* fallback to status text */ }
        throw new Error(errorMessage);
    }
    return response.status === 204 ? null : response.json();
};

function useMailList() {
    const [listItems, setListItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const API_URL = '/mail-list';

    const refetch = async (search = '', page = 1, limit = 10) => {
        setLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({ search, page: page.toString(), limit: limit.toString() });
            const data = await apiFetch(`${API_URL}?${params.toString()}`);
            setListItems(data?.data || []);
        } catch (err) {
            setError(err);
            setListItems([]);
        } finally {
            setLoading(false);
        }
    };

    const deleteItems = async (ids) => {
        if (!Array.isArray(ids) || ids.length === 0) return;
        return apiFetch(`${API_URL}/bulk`, { method: 'DELETE', body: { ids } });
    };

    const saveItem = async (item) => {
        const { id, email, firstName, lastName, role, companyName } = item;
        return apiFetch(`${API_URL}/${id}`, {
            method: 'PUT',
            body: { email, firstName, lastName, role, companyName },
        });
    };

    const addItem = async (data) => {
        return apiFetch(API_URL, { method: 'POST', body: data });
    };

    useEffect(() => { refetch(); }, []);

    return { listItems, loading, error, refetch, deleteItems, saveItem, addItem };
}

// --------------------------------------------------------------------------
// 2. MAIN COMPONENT
// --------------------------------------------------------------------------

export default function MailList() {
    const { listItems, loading, error, refetch, deleteItems, saveItem, addItem } = useMailList();

    const [page, setPage] = useState(0);
    const [order, setOrder] = useState('asc');
    const [selected, setSelected] = useState([]);
    const [orderBy, setOrderBy] = useState('email');
    const [filterName, setFilterName] = useState('');
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [isAddFormOpen, setIsAddFormOpen] = useState(false);
    const [isEditFormOpen, setIsEditFormOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

    const isAdmin = true; // Assuming admin check is handled elsewhere

    const TABLE_HEAD = [
        { id: 'email', label: 'Email Address', align: 'left' },
        { id: 'name', label: 'Contact Name', align: 'left' },
        { id: 'companyName', label: 'Company', align: 'left' },
        { id: 'role', label: 'Group', align: 'left' },
        { id: 'isInvited', label: 'Status', align: 'left' },
        { id: '' },
    ];

    // --- HANDLERS ---
    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleSelectAllClick = (event) => {
        if (event.target.checked) {
            const newSelecteds = listItems.map((n) => n.id);
            setSelected(newSelecteds);
            return;
        }
        setSelected([]);
    };

    const handleClick = (event, id) => {
        const selectedIndex = selected.indexOf(id);
        let newSelected = [];
        if (selectedIndex === -1) newSelected = newSelected.concat(selected, id);
        else if (selectedIndex === 0) newSelected = newSelected.concat(selected.slice(1));
        else if (selectedIndex === selected.length - 1) newSelected = newSelected.concat(selected.slice(0, -1));
        else if (selectedIndex > 0) newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
        setSelected(newSelected);
    };

    const handleFilterByName = (event) => {
        const val = event.target.value;
        setFilterName(val);
        refetch(val, 1, rowsPerPage);
    };

    const handleDeleteClick = async () => {
        try {
            await deleteItems(selected);
            setSnackbarMessage('Items deleted successfully');
            setSnackbarSeverity('success');
            setSelected([]);
            refetch(filterName, page + 1, rowsPerPage);
        } catch (err) {
            setSnackbarMessage(err.message);
            setSnackbarSeverity('error');
        } finally { setOpenSnackbar(true); setOpenDeleteDialog(false); }
    };

    const handleSaveItem = async () => {
        try {
            await saveItem(editingItem);
            setSnackbarMessage('Entry updated successfully');
            setSnackbarSeverity('success');
            setIsEditFormOpen(false);
            refetch(filterName, page + 1, rowsPerPage);
        } catch (err) {
            setSnackbarMessage(err.message);
            setSnackbarSeverity('error');
        } finally { setOpenSnackbar(true); }
    };

    // --- RENDER HELPERS ---
    const getComparator = (order, orderBy) => (a, b) => {
        if (b[orderBy] < a[orderBy]) return order === 'desc' ? -1 : 1;
        if (b[orderBy] > a[orderBy]) return order === 'desc' ? 1 : -1;
        return 0;
    };

    const sortedItems = [...listItems].sort(getComparator(order, orderBy));

    return (
        <Page title="Mail List Admin">
            <Container maxWidth={false}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
                    <Typography variant="h4" gutterBottom>Mailing List & Invitations</Typography>
                    <Button variant="contained" onClick={() => setIsAddFormOpen(true)} startIcon={<Iconify icon="eva:plus-fill" />}>
                        Add Entry
                    </Button>
                </Stack>

                <Card>
                    <UserListToolbar 
                        numSelected={selected.length} 
                        filterName={filterName} 
                        onFilterName={handleFilterByName} 
                        onDeleteSelected={() => setOpenDeleteDialog(true)} 
                    />

                    <TableContainer sx={{ minWidth: 800, maxHeight: 600 }}>
                        <Table stickyHeader>
                            <UserListHead
                                order={order}
                                orderBy={orderBy}
                                headLabel={TABLE_HEAD}
                                rowCount={listItems.length}
                                numSelected={selected.length}
                                onRequestSort={handleRequestSort}
                                onSelectAllClick={handleSelectAllClick}
                            />
                            <TableBody>
                                {loading && (
                                    <TableRow><TableCell colSpan={6} align="center"><CircularProgress sx={{ my: 3 }} /></TableCell></TableRow>
                                )}
                                {!loading && sortedItems.map((row) => {
                                    const { id, email, firstName, lastName, companyName, role, isInvited } = row;
                                    const isItemSelected = selected.indexOf(id) !== -1;

                                    return (
                                        <TableRow hover key={id} tabIndex={-1} role="checkbox" selected={isItemSelected}>
                                            <TableCell padding="checkbox">
                                                <Checkbox checked={isItemSelected} onChange={(event) => handleClick(event, id)} />
                                            </TableCell>
                                            <TableCell>{email}</TableCell>
                                            <TableCell>{`${firstName || ''} ${lastName || ''}`}</TableCell>
                                            <TableCell>{companyName || '-'}</TableCell>
                                            <TableCell sx={{ textTransform: 'capitalize' }}>{role}</TableCell>
                                            <TableCell>
                                                <Chip 
                                                    label={isInvited ? "Pending" : "Active"} 
                                                    color={isInvited ? "warning" : "success"} 
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell align="right">
                                                <IconButton onClick={() => { setEditingItem(row); setIsEditFormOpen(true); }}>
                                                    <Iconify icon="eva:edit-fill" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25]}
                        component="div"
                        count={listItems.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={(e, p) => setPage(p)}
                        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                    />
                </Card>
            </Container>

            {/* --- DIALOGS --- */}

            {/* Edit Dialog */}
            <Dialog open={isEditFormOpen} onClose={() => setIsEditFormOpen(false)} fullWidth maxWidth="xs">
                <DialogTitle>Edit Entry</DialogTitle>
                <DialogContent sx={{ pt: 2 }}>
                    <Stack spacing={2}>
                        <TextField fullWidth label="First Name" value={editingItem?.firstName || ''} onChange={(e) => setEditingItem({...editingItem, firstName: e.target.value})} />
                        <TextField fullWidth label="Last Name" value={editingItem?.lastName || ''} onChange={(e) => setEditingItem({...editingItem, lastName: e.target.value})} />
                        <TextField fullWidth label="Company" value={editingItem?.companyName || ''} onChange={(e) => setEditingItem({...editingItem, companyName: e.target.value})} />
                        <FormControl fullWidth>
                            <InputLabel>Role/Group</InputLabel>
                            <Select value={editingItem?.role || 'client'} label="Role/Group" onChange={(e) => setEditingItem({...editingItem, role: e.target.value})}>
                                <MenuItem value="client">Client</MenuItem>
                                <MenuItem value="vendor">Vendor</MenuItem>
                                <MenuItem value="marketing">Marketing</MenuItem>
                            </Select>
                        </FormControl>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsEditFormOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSaveItem}>Save Changes</Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation */}
            <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>Are you sure you want to delete {selected.length} items?</DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
                    <Button color="error" variant="contained" onClick={handleDeleteClick}>Delete</Button>
                </DialogActions>
            </Dialog>

            {/* Add New Entry Dialog (Mini-form) */}
            <AddEntryModal 
                open={isAddFormOpen} 
                onClose={() => { setIsAddFormOpen(false); refetch(); }} 
                addItem={addItem} 
            />

            <Snackbar open={openSnackbar} autoHideDuration={4000} onClose={() => setOpenSnackbar(false)}>
                <Alert severity={snackbarSeverity}>{snackbarMessage}</Alert>
            </Snackbar>
        </Page>
    );
}

// Separate internal component for Add Modal to keep main logic clean
function AddEntryModal({ open, onClose, addItem }) {
    const [form, setForm] = useState({ email: '', firstName: '', lastName: '', companyName: '', role: 'client' });
    const handleAdd = async () => {
        try {
            await addItem(form);
            onClose();
        } catch (e) { alert(e.message); }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
            <DialogTitle>New Mailing List Entry</DialogTitle>
            <DialogContent sx={{ pt: 2 }}>
                <Stack spacing={2}>
                    <TextField fullWidth label="Email Address" required value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} />
                    <TextField fullWidth label="First Name" value={form.firstName} onChange={(e) => setForm({...form, firstName: e.target.value})} />
                    <TextField fullWidth label="Last Name" value={form.lastName} onChange={(e) => setForm({...form, lastName: e.target.value})} />
                    <TextField fullWidth label="Company" value={form.companyName} onChange={(e) => setForm({...form, companyName: e.target.value})} />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" onClick={handleAdd}>Add Entry</Button>
            </DialogActions>
        </Dialog>
    );
}